// YERİ: src/app/domains/site/sections/agency.sections.ts
// AJANS'a özel Sinco-RUHU bölümler (kod kopyalanmadı, özgün). Tema CSS vars ile
// renklenir (violet + cyan). Diğer sektörlerin generic bölümlerinden GÖRSEL olarak
// belirgin biçimde farklı: yüzen dashboard'lu hero, renkli ikon kartları, gradient
// sayaç bandı, gradient CTA. Yeni tipler: agency-hero / agency-services /
// agency-counter / agency-cta (SectionHost @switch'e kayıtlı).

import { Component, computed, inject, input } from '@angular/core';
import { MIcon } from '@/app/shared/ui/m-icon';
import { SiteChromeText } from '../core/site-chrome-text';
import { SiteConfig, cfg } from './section.model';

interface StatItem { value: string; label: string; }
interface ServiceItem { icon?: string; title: string; description?: string; }

// ── HERO: yüzen istatistik/dashboard görseli ────────────────────────────────────
@Component({
  selector: 'sec-agency-hero',
  standalone: true,
  template: `
    <section class="relative overflow-hidden bg-white dark:bg-neutral-950">
      <!-- yumuşak renk lekeleri -->
      <div class="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full opacity-20 blur-3xl" style="background-color:var(--site-primary)"></div>
      <div class="pointer-events-none absolute right-0 top-40 h-80 w-80 rounded-full opacity-20 blur-3xl" style="background-color:var(--site-accent)"></div>

      <div class="relative mx-auto grid max-w-7xl items-center gap-14 px-6 py-20 lg:grid-cols-2 lg:px-8 lg:py-28">
        <!-- SOL: metin -->
        <div>
          @if (badge()) {
            <span class="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest"
                  style="background-color:color-mix(in srgb, var(--site-primary) 12%, transparent); color:var(--site-primary)">
              <span class="h-1.5 w-1.5 rounded-full" style="background-color:var(--site-accent)"></span>
              {{ badge() }}
            </span>
          }
          <h1 class="font-[family-name:var(--site-head-font)] text-4xl font-bold leading-[1.08] tracking-tight text-neutral-900 dark:text-white sm:text-5xl lg:text-6xl">
            {{ headline() }}
            @if (highlight()) {
              <span class="relative ml-2 inline-block">
                <span style="color:var(--site-primary)">{{ highlight() }}</span>
                <span class="absolute -bottom-1 left-0 h-2 w-full rounded-full opacity-40" style="background-color:var(--site-accent)"></span>
              </span>
            }
          </h1>
          @if (subtext()) {
            <p class="mt-6 max-w-xl text-lg leading-relaxed text-neutral-600 dark:text-neutral-300">{{ subtext() }}</p>
          }
          <div class="mt-9 flex flex-col gap-3 sm:flex-row">
            @if (primaryCtaText()) {
              <a [href]="primaryCtaUrl()"
                 class="ws-btn inline-flex items-center justify-center gap-2 rounded-xl px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:opacity-95"
                 style="background-color:var(--site-primary)">
                {{ primaryCtaText() }} <m-icon name="arrow-right" size="sm" />
              </a>
            }
            @if (secondaryCtaText()) {
              <a [href]="secondaryCtaUrl()"
                 class="ws-btn inline-flex items-center justify-center gap-2 rounded-xl border-2 px-7 py-3.5 text-sm font-semibold transition hover:bg-neutral-50 dark:hover:bg-neutral-900"
                 style="border-color:color-mix(in srgb, var(--site-primary) 30%, transparent); color:var(--site-primary)">
                <m-icon name="play" size="sm" /> {{ secondaryCtaText() }}
              </a>
            }
          </div>
        </div>

        <!-- SAĞ: yüzen "dashboard" görseli -->
        <div class="relative">
          <div class="rounded-3xl border border-neutral-200 bg-white p-6 shadow-2xl dark:border-neutral-800 dark:bg-neutral-900">
            <div class="mb-4 flex items-center justify-between">
              <div>
                <p class="text-xs font-medium uppercase tracking-wider text-neutral-400">{{ text.t('ag.performance') }}</p>
                <p class="font-[family-name:var(--site-head-font)] text-2xl font-bold text-neutral-900 dark:text-white">{{ chartTitle() }}</p>
              </div>
              <span class="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
                    style="background-color:color-mix(in srgb, var(--site-accent) 18%, transparent); color:var(--site-primary)">
                <m-icon name="trending-up" size="xs" /> +{{ chartDelta() }}
              </span>
            </div>
            <!-- faux bar chart -->
            <div class="flex h-40 items-end gap-2.5">
              @for (h of bars(); track $index) {
                <div class="flex-1 rounded-t-md transition-all"
                     [style.height.%]="h"
                     [style.background-color]="$index % 2 === 0 ? 'var(--site-primary)' : 'var(--site-accent)'"
                     [style.opacity]="0.55 + ($index / 20)"></div>
              }
            </div>
          </div>

          <!-- yüzen pill kartlar -->
          <div class="absolute -left-5 top-8 hidden rounded-2xl border border-neutral-200 bg-white px-4 py-3 shadow-xl dark:border-neutral-800 dark:bg-neutral-900 sm:block">
            <p class="font-[family-name:var(--site-head-font)] text-xl font-bold" style="color:var(--site-primary)">{{ stat1Value() }}</p>
            <p class="text-[11px] font-medium text-neutral-500">{{ stat1Label() }}</p>
          </div>
          <div class="absolute -bottom-5 right-6 hidden items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-4 py-3 shadow-xl dark:border-neutral-800 dark:bg-neutral-900 sm:flex">
            <span class="flex size-9 items-center justify-center rounded-xl text-white" style="background-color:var(--site-accent)"><m-icon name="check" size="sm" /></span>
            <div>
              <p class="font-[family-name:var(--site-head-font)] text-lg font-bold text-neutral-900 dark:text-white">{{ stat2Value() }}</p>
              <p class="text-[11px] font-medium text-neutral-500">{{ stat2Label() }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  `,
  imports: [MIcon],
})
export class AgencyHeroSection {
  /** Ziyaretcinin dilindeki kabuk metni (bolum ICERIGI CMS'ten gelir). */
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected badge = computed(() => cfg(this.config(), 'badge', this.text.t('fb.veriTasarimBuyume')));
  protected headline = computed(() => cfg(this.config(), 'headline', this.text.t('fb.markaniziDijitalde')));
  protected highlight = computed(() => cfg(this.config(), 'highlight', this.text.t('fb.buyutuyoruz')));
  protected subtext = computed(() => cfg(this.config(), 'subtext', this.text.t('fb.stratejiTasarimVeVeriyi')));
  protected primaryCtaText = computed(() => cfg(this.config(), 'primaryCtaText', 'Teklif Al'));
  protected primaryCtaUrl = computed(() => cfg(this.config(), 'primaryCtaUrl', '#contact'));
  protected secondaryCtaText = computed(() => cfg(this.config(), 'secondaryCtaText', this.text.t('fb.calismalarimiz')));
  protected secondaryCtaUrl = computed(() => cfg(this.config(), 'secondaryCtaUrl', '#services'));
  protected chartTitle = computed(() => cfg(this.config(), 'chartTitle', '₺2.4M'));
  protected chartDelta = computed(() => cfg(this.config(), 'chartDelta', '180%'));
  protected stat1Value = computed(() => cfg(this.config(), 'stat1Value', '250+'));
  protected stat1Label = computed(() => cfg(this.config(), 'stat1Label', 'Proje'));
  protected stat2Value = computed(() => cfg(this.config(), 'stat2Value', '90+'));
  protected stat2Label = computed(() => cfg(this.config(), 'stat2Label', 'Mutlu Marka'));
  protected bars = computed<number[]>(() => [38, 52, 45, 68, 60, 82, 74, 95]);
}

// ── SERVICES: renkli ikon kartları (Sinco "iconbox" ruhu) ───────────────────────
@Component({
  selector: 'sec-agency-services',
  standalone: true,
  template: `
    <section id="services" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="mx-auto max-w-2xl text-center">
          <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mt-4 text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <div class="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          @for (item of items(); track item.title; let i = $index) {
            <div class="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white p-7 transition hover:-translate-y-1.5 hover:shadow-2xl dark:border-neutral-800 dark:bg-neutral-950">
              <!-- üst gradient şerit (hover'da görünür) -->
              <div class="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 transition-transform duration-300 group-hover:scale-x-100"
                   style="background-image:linear-gradient(90deg, var(--site-primary), var(--site-accent))"></div>
              <div class="flex size-14 items-center justify-center rounded-2xl text-white shadow-lg transition group-hover:scale-110"
                   [style.background-image]="i % 2 === 0 ? 'linear-gradient(135deg, var(--site-primary), var(--site-accent))' : 'linear-gradient(135deg, var(--site-accent), var(--site-primary))'">
                <m-icon [name]="item.icon || 'sparkles'" size="md" />
              </div>
              <h3 class="mt-5 text-lg font-bold text-neutral-900 dark:text-white">{{ item.title }}</h3>
              @if (item.description) { <p class="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ item.description }}</p> }
              <span class="mt-4 inline-flex items-center gap-1 text-sm font-semibold opacity-0 transition group-hover:opacity-100" style="color:var(--site-primary)">
                {{ text.t('ag.detail') }} <m-icon name="arrow-right" size="xs" />
              </span>
            </div>
          }
        </div>
      </div>
    </section>
  `,
  imports: [MIcon],
})
export class AgencyServicesSection {
  /** Ziyaretcinin dilindeki kabuk metni (bolum ICERIGI CMS'ten gelir). */
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', 'Hizmetlerimiz'));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.uctanUcaDijitalCozumler')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', this.text.t('fb.markanizinIhtiyaciOlanHer')));
  protected items = computed<ServiceItem[]>(() => cfg<ServiceItem[]>(this.config(), 'items', [
    { icon: 'palette', title: 'Marka & Kimlik', description: 'Logo, kurumsal kimlik ve marka stratejisi.' },
    { icon: 'code', title: 'Web & Mobil', description: this.text.t('fb.hizliModernDonusumOdakli') },
    { icon: 'megaphone', title: 'Performans Pazarlama', description: this.text.t('fb.olceklenebilirReklamVeDonusum') },
    { icon: 'chart-bar', title: 'Veri & Analitik', description: this.text.t('fb.panolarVeIcgorulerleDaha') },
    { icon: 'search', title: this.text.t('fb.seoIcerik'), description: this.text.t('fb.organikGorunurlukVeIcerik') },
    { icon: 'rocket', title: this.text.t('fb.buyumeOtomasyon'), description: this.text.t('fb.funnelKurulumuVeSurekli') },
  ]));
}

// ── COUNTER: gradient istatistik bandı ──────────────────────────────────────────
@Component({
  selector: 'sec-agency-counter',
  standalone: true,
  template: `
    <section class="relative overflow-hidden py-20"
             style="background-image:linear-gradient(120deg, var(--site-primary-dark), var(--site-primary))">
      <div class="pointer-events-none absolute inset-0 opacity-[0.08] bg-[linear-gradient(135deg,#fff_1px,transparent_1px),linear-gradient(45deg,#fff_1px,transparent_1px)] bg-[size:26px_26px]"></div>
      <div class="relative mx-auto grid max-w-7xl grid-cols-2 gap-8 px-6 lg:grid-cols-4 lg:px-8">
        @for (s of items(); track s.label) {
          <div class="text-center">
            <p class="font-[family-name:var(--site-head-font)] text-4xl font-bold text-white sm:text-5xl">{{ s.value }}</p>
            <span class="mx-auto mt-3 block h-1 w-10 rounded-full" style="background-color:var(--site-accent)"></span>
            <p class="mt-3 text-sm font-medium uppercase tracking-wider text-white/75">{{ s.label }}</p>
          </div>
        }
      </div>
    </section>
  `,
})
export class AgencyCounterSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected items = computed<StatItem[]>(() => cfg<StatItem[]>(this.config(), 'items', [
    { value: '250+', label: 'Tamamlanan Proje' }, { value: '%180', label: this.text.t('fb.ortBuyume') },
    { value: '90+', label: 'Mutlu Marka' }, { value: '12', label: this.text.t('fb.yillikDeneyim') },
  ]));
}

// ── CTA: gradient "konuşalım" bandı ─────────────────────────────────────────────
@Component({
  selector: 'sec-agency-cta',
  standalone: true,
  template: `
    <section class="px-6 py-20 lg:px-8">
      <div class="relative mx-auto max-w-5xl overflow-hidden rounded-3xl px-8 py-16 text-center shadow-2xl"
           style="background-image:linear-gradient(120deg, var(--site-primary), var(--site-primary-dark))">
        <div class="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-25 blur-2xl" style="background-color:var(--site-accent)"></div>
        <div class="relative">
          <h2 class="font-[family-name:var(--site-head-font)] text-3xl font-bold text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtext()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-white/80">{{ subtext() }}</p> }
          <a [href]="ctaUrl()" class="ws-btn mt-9 inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-sm font-bold shadow-lg transition hover:-translate-y-0.5"
             style="color:var(--site-primary)">
            {{ ctaText() }} <m-icon name="arrow-right" size="sm" />
          </a>
        </div>
      </div>
    </section>
  `,
  imports: [MIcon],
})
export class AgencyCtaSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.projeniziKonusalim')));
  protected subtext = computed(() => cfg(this.config(), 'subtext', this.text.t('fb.hedefleriniziAnlatinSizeOzel')));
  protected ctaText = computed(() => cfg(this.config(), 'ctaText', this.text.t('fb.ucretsizGorusme')));
  protected ctaUrl = computed(() => cfg(this.config(), 'ctaUrl', '#contact'));
}
